// STUB(revamp-accounts) FILE: the whole file is stubbed data, removed by `node .claude/scripts/remove-stubs.mjs revamp-accounts`
// Review aid: one fake account per challenge detail scenario of the revamp (1-Step, 2-Step Bright and F1KC; ACTIVE,
// FUNDED and BREACHED; started before or after the 16 Nov cutoff). Open one by URL, for example
// /dashboard/challenges/stub-f1kc-breached-old-design. The id says which design the page should show; F1KC always
// keeps the old 1-Step design. No backend is called for these ids. All 18 are also the first cards on the dashboard
// slider and the accounts page (REVAMP_STUB_ACCOUNTS).
import type {
  BasicChallenge,
  Challenge,
  ChallengePhase,
  ChallengeStatus,
  ChallengeTrade,
  ChallengeTradesPaginated,
  PaginationPayload,
  Planet,
} from "@models";
import { Observable, of } from "rxjs";
import type { LegacyResponse } from "../../core/service/response.model";
import type { BackendEvaluationType } from "../model/evaluationTypeMapping.model";

type RevampStubKind = "1step" | "2step-bright" | "f1kc";
type RevampStubState = "active" | "funded" | "breached";

interface RevampStubRules {
  evaluationType: BackendEvaluationType;
  dailyLossRatio: number;
  maxLossRatio: number;
  profitTargetRatio: number;
  minTradingDays: number;
}

interface RevampStubSpec {
  id: string;
  kind: RevampStubKind;
  state: RevampStubState;
  isAfterCutoff: boolean;
  accountNumber: string;
  accountSize: number;
}

const F1KC_LABEL: string = "F1KC";
/** What the backend sends as the profit target of a funded account. */
const FUNDED_PROFIT_TARGET: number = 2147483647;
const START_DATE_BEFORE_CUTOFF: string = "2026-11-03T09:00:00";
const START_DATE_AFTER_CUTOFF: string = "2026-11-20T09:00:00";

const RULES: Record<RevampStubKind, RevampStubRules> = {
  "1step": {
    evaluationType: "ONE_STEP",
    dailyLossRatio: 0.03,
    maxLossRatio: 0.06,
    profitTargetRatio: 0.1,
    minTradingDays: 5,
  },
  "2step-bright": {
    evaluationType: "BRIGHT",
    dailyLossRatio: 0.05,
    maxLossRatio: 0.1,
    profitTargetRatio: 0.08,
    minTradingDays: 5,
  },
  f1kc: {
    evaluationType: "ONE_STEP",
    dailyLossRatio: 0.03,
    maxLossRatio: 0.06,
    profitTargetRatio: 0.1,
    minTradingDays: 5,
  },
};

const PLANETS: Record<RevampStubKind, Planet> = {
  "1step": "MARS",
  "2step-bright": "SATURN",
  f1kc: "PLUTO",
};

const ACCOUNT_SIZES: Record<RevampStubKind, number> = {
  "1step": 10000,
  "2step-bright": 50000,
  f1kc: 1000,
};

const KINDS: RevampStubKind[] = ["1step", "2step-bright", "f1kc"];
const STATES: RevampStubState[] = ["active", "funded", "breached"];

function buildId(kind: RevampStubKind, state: RevampStubState, isAfterCutoff: boolean): string {
  if (!isAfterCutoff) return `stub-${kind}-${state}-old-design`;
  // F1KC keeps the old design after the cutoff too, the id says so.
  return kind === "f1kc"
    ? `stub-${kind}-${state}-after-cutoff-old-design`
    : `stub-${kind}-${state}-new-design`;
}

const SPECS: RevampStubSpec[] = KINDS.flatMap((kind: RevampStubKind, kindIndex: number) =>
  [false, true].flatMap((isAfterCutoff: boolean, cutoffIndex: number) =>
    STATES.map((state: RevampStubState, stateIndex: number) => ({
      id: buildId(kind, state, isAfterCutoff),
      kind,
      state,
      isAfterCutoff,
      accountNumber: `998${kindIndex}${cutoffIndex}${stateIndex}0`,
      accountSize: ACCOUNT_SIZES[kind],
    })),
  ),
);

/** Every stub URL path, in the order of the scenario list (handy for a review round). */
export const REVAMP_STUB_IDS: string[] = SPECS.map((spec: RevampStubSpec) => spec.id);

const STUB_TRADES: ChallengeTrade[] = [
  {
    tradeId: "stub-revamp-trade-1",
    positionId: "stub-revamp-position-1",
    action: "BUY",
    currency: "USD",
    symbol: "EURUSD",
    volume: 0.5,
    openTime: Date.parse("2026-11-03T08:15:00Z"),
    closeTime: Date.parse("2026-11-03T10:40:00Z"),
    openPrice: 1.0832,
    closePrice: 1.0861,
    commission: -3.5,
    swapFees: 0,
    profit: 145,
  },
  {
    tradeId: "stub-revamp-trade-2",
    positionId: "stub-revamp-position-2",
    action: "SELL",
    currency: "USD",
    symbol: "XAUUSD",
    volume: 0.1,
    openTime: Date.parse("2026-11-03T13:05:00Z"),
    closeTime: Date.parse("2026-11-03T14:20:00Z"),
    openPrice: 2681.4,
    closePrice: 2684.9,
    commission: -1,
    swapFees: 0,
    profit: -35,
  },
];

// Breached accounts are breached in Phase 1, on max loss (balance just under the limit).
function getPhase(spec: RevampStubSpec): ChallengePhase {
  return spec.state === "funded" ? "FUNDED" : "PHASE1";
}

function getAccountState(spec: RevampStubSpec): ChallengeStatus {
  return spec.state === "breached" ? "BREACHED" : "ACTIVE";
}

function getBalance(spec: RevampStubSpec): number {
  const size: number = spec.accountSize;
  if (spec.state === "breached") return size * (1 - RULES[spec.kind].maxLossRatio) - size * 0.002;
  return size * (spec.state === "funded" ? 1.0185 : 1.035);
}

function getDisplayAccountNumber(spec: RevampStubSpec): string {
  return spec.state === "funded" ? `Funded-${spec.accountNumber}` : spec.accountNumber;
}

function getStartDate(spec: RevampStubSpec): string {
  return spec.isAfterCutoff ? START_DATE_AFTER_CUTOFF : START_DATE_BEFORE_CUTOFF;
}

function getLabels(spec: RevampStubSpec): string[] {
  return spec.kind === "f1kc" ? [F1KC_LABEL] : [];
}

function toBasicChallenge(spec: RevampStubSpec): BasicChallenge {
  return {
    id: spec.id,
    userId: "stub-user",
    planetName: PLANETS[spec.kind],
    accountNumber: getDisplayAccountNumber(spec),
    subscriptionType: getPhase(spec),
    accountSize: spec.accountSize,
    currentBalance: getBalance(spec),
    accountCurrency: "USD",
    startDate: getStartDate(spec),
    accountState: getAccountState(spec),
    isDisplay: true,
    chargeSwapFees: true,
    user: { login: spec.accountNumber, password: "stub-password" },
    type: "MT5",
    evaluationType: RULES[spec.kind].evaluationType,
    labels: getLabels(spec),
  };
}

/** The 18 stub cards, shown before every other account on the dashboard slider and the accounts page. */
export const REVAMP_STUB_ACCOUNTS: BasicChallenge[] = SPECS.map(toBasicChallenge);

function toChallenge(spec: RevampStubSpec): Challenge {
  const rules: RevampStubRules = RULES[spec.kind];
  const size: number = spec.accountSize;
  const isFunded: boolean = spec.state === "funded";
  const isBreached: boolean = spec.state === "breached";
  const balance: number = getBalance(spec);
  const balanceAtStartOfDay: number = isBreached ? size * 0.952 : size * 1.028;
  const equity: number = isBreached ? balance : balance - size * 0.004;
  const profit: number = balance - size;

  return {
    challengeId: spec.id,
    accountNumber: getDisplayAccountNumber(spec),
    planetName: PLANETS[spec.kind],
    subscriptionType: getPhase(spec),
    chargeSwapFees: true,
    accountState: getAccountState(spec),
    visible: true,
    startDate: getStartDate(spec),
    accountCurrency: "USD",
    balance,
    netProfitLoss: profit,
    equity,
    balanceAtStartOfDay,
    // Breach levels, as the backend sends them
    maxPermittedLoss: size * (1 - rules.maxLossRatio),
    dailyPermittedLoss: balanceAtStartOfDay * (1 - rules.dailyLossRatio),
    dailyProfit: balance - balanceAtStartOfDay,
    minimumTradingDaysRequired: rules.minTradingDays,
    minimumTradingDaysValue: 3,
    maxTodayTradedLossRequired: -balanceAtStartOfDay * rules.dailyLossRatio,
    maxTodayTradedLossValue: Math.min(0, equity - balanceAtStartOfDay),
    maxOverallTradedLossRequired: -size * rules.maxLossRatio,
    maxOverallTradedLossValue: Math.min(0, equity - size),
    maxProfitTargetRequested: isFunded ? FUNDED_PROFIT_TARGET : size * rules.profitTargetRatio,
    maxProfitTargetValue: Math.max(0, profit),
    openPositionsCount: isBreached ? 0 : 1,
    voucher: null,
    addons: [],
    type: "MT5",
    userDetails: {
      login: spec.accountNumber,
      password: "stub-password",
      investorPassword: "stub-investor",
      serverName: "Stub-Server",
    },
    initialAccountBalance: size,
    equityAtStartOfDay: balanceAtStartOfDay,
    accountLogin: spec.accountNumber,
    accountPassword: "stub-password",
    evaluationType: rules.evaluationType,
    labels: getLabels(spec),
  };
}

export function isRevampStubId(challengeId: string): boolean {
  return REVAMP_STUB_IDS.includes(challengeId);
}

/** Fake `GET /challenges/:id`. */
export function getRevampStubChallengeResponse$(
  challengeId: string,
): Observable<LegacyResponse<Challenge>> {
  const spec: RevampStubSpec =
    SPECS.find((item: RevampStubSpec) => item.id === challengeId) ?? SPECS[0];

  return of({ data: toChallenge(spec), msg: "stub", status: 200 });
}

/** Fake `GET /trades/history/:id`, one page with the static rows. */
export function getRevampStubTrades$(
  paginationPayload: PaginationPayload,
): Observable<ChallengeTradesPaginated> {
  return of({
    items: STUB_TRADES,
    totalItems: STUB_TRADES.length,
    pageInfo: {
      currentPage: paginationPayload.page,
      size: paginationPayload.size,
      first: true,
      last: true,
      totalPages: 1,
    },
  });
}
